import {FC, useMemo, useState} from 'react';
import Input from '@/shared/components/Inputs/Input';
import Button from '@/shared/components/Button';
import {BODY2, H6, SUBTITLE1} from '@/utils/constants';
import {Accordion, AccordionDetails, AccordionSummary, Box, Typography} from '@mui/material';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
import {ReactComponent as PlusIcon} from '@icons/plus.svg';
import {ReactComponent as MinusIcon} from '@icons/minus.svg';
import {supportStyle} from './HelpAndSupport.styles';
import {FaqSection, supportData} from './supportData';

const UserHelpAndSupport: FC = () => {
  const emailTo = process.env.REACT_APP_EMAIL_TO;

  const [searchValue, setSearchValue] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const filteredSections = useMemo<FaqSection[]>(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return supportData;

    return supportData
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => item.title.toLowerCase().includes(query) || item.description.toLowerCase().includes(query)
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [searchValue]);

  const handleContactUs = () => {
    const subject = encodeURIComponent('EventSimply support request');
    window.location.href = `mailto:${emailTo}?subject=${subject}`;
  };

  return (
    <Box sx={supportStyle.wrapper}>
      <Box>
        <Typography sx={supportStyle.title} variant={H6}>
          Frequently Asked Questions
        </Typography>
        <Typography sx={supportStyle.afterTitle}>
          Need help? Here are answers to the questions guests ask most.
        </Typography>
        <Box sx={supportStyle.search}>
          <SearchIcon />
          <Input placeholder="Search for answers" value={searchValue} onChange={setSearchValue} />
        </Box>

        <Box sx={supportStyle.wrapperList}>
          {filteredSections.map((section) => (
            <Box key={section.id} sx={supportStyle.section}>
              <Typography sx={supportStyle.sectionTitle} variant={SUBTITLE1}>
                {section.title}
              </Typography>

              {section.items.map((item) => (
                <Accordion
                  sx={supportStyle.wrapperItem}
                  key={item.id}
                  expanded={expanded === item.id}
                  onChange={handleChange(item.id)}
                >
                  <AccordionSummary
                    expandIcon={
                      expanded === item.id ? <MinusIcon style={{stroke: 'var(--primary-500)'}} /> : <PlusIcon />
                    }
                    aria-controls={`${item.id}-content`}
                    id={item.id}
                  >
                    <Typography
                      sx={{
                        ...(expanded === item.id && supportStyle.accent),
                      }}
                      variant={SUBTITLE1}
                    >
                      {item.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant={BODY2}>{item.description}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))}

          {filteredSections.length === 0 && <Typography variant={BODY2}>No results found</Typography>}
        </Box>
      </Box>

      <Box sx={supportStyle.buttonWrapper}>
        <Box>
          <Typography variant={SUBTITLE1}>Technical support</Typography>
          <Typography variant={BODY2} sx={supportStyle.text}>
            If you have some additional question, please contact our Help Center
          </Typography>
        </Box>

        <Button sx={supportStyle.button} title="Contact us" onClick={handleContactUs} />
      </Box>
    </Box>
  );
};

export default UserHelpAndSupport;
